import { ColumnDef } from "@tanstack/react-table";
import { ChevronDownIcon, SlidersHorizontal } from "lucide-react";
import { FilterIcon, ShareUpIcon } from "@/components/icons";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LiveDot } from "@/components/ui/live-dot";
import NumericText from "@/components/NumericText";
import type { StudentInAssignmentEntity } from "@/types/canonical";
import { formatDateTime } from "@/shared/dateFormat";
import { isCompletedTimeAlwaysVisible } from "@/lib/branding";
import { getLastAttempt } from "@/app/assignments/[id]/utils/lastAttempt";
import { ScoreCell } from "./ScoreCell";
import { ViolationsCell } from "./ViolationsCell";

type TranslateFn = (
  key: string,
  values?: Record<string, string | number | Date>
) => string;

type StudentTableMeta = {
  assignmentId?: number | string;
  onOpenSettingsDrawer?: (
    student: StudentInAssignmentEntity,
    mode: "view" | "edit"
  ) => void;
  onCopyReportLink?: (studentId: number | string) => void;
  onSelectAttempt?: (studentId: number | string, attemptId: number | null) => void;
  selectedAttemptByStudentId?: Record<number, number | null>;
  trustScoreByAttemptId?: Record<number, number | null>;
  isTrustScoresUnavailable?: boolean;
  /** Gate for the "Индивидуальные настройки задания" trigger; false for proctors. */
  canEditSettings?: boolean;
  /** True only for teachers — hides the report-link (↗) action; proctor/manager keep it. */
  isTeacher?: boolean;
  /** Assignment type (e.g. "quiz", "external") to toggle external-assignment behavior. */
  assignmentType?: string;
};

const getStudentTableMeta = (meta: unknown): StudentTableMeta | undefined =>
  meta && typeof meta === "object" ? (meta as StudentTableMeta) : undefined;

export const EXTERNAL_STUDENT_LABEL_SCHOOL_ID = 19;

const buildSchoolStudentLabel = (
  user: StudentInAssignmentEntity["user"]
): string => {
  const fio = [user.firstname, user.lastname, user.patronymic]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean)
    .join(" ");
  const id =
    user.external_id == null || String(user.external_id).trim() === ""
      ? ""
      : String(user.external_id).trim();
  const email = user.email && user.email.trim() ? user.email.trim() : "";
  return [fio, id, email].filter(Boolean).join(" · ");
};

/**
 * Violations counter in the row is off until we agree on how violations are
 * presented. Flip to true to bring it back — the query and the cell stay in
 * place, and nothing is requested while this is false.
 */
const SHOW_VIOLATIONS: boolean = false;

export const getStudentColumns = (
  t: TranslateFn,
  schoolId?: number | null,
  liveStudentIds: Set<number> = new Set()
): ColumnDef<StudentInAssignmentEntity>[] => [
    {
      accessorKey: "user",
      header: t("label-class-students"),
      cell: ({ row }) => {
        const user = row.original.user ?? ({} as StudentInAssignmentEntity["user"]);
        const { firstname, lastname, photo } = user;
        const initials = `${lastname?.[0] ?? ""}${firstname?.[0] ?? ""}`;
        /* lastname first, so the column reads and scans by surname */
        const fullName =
          [lastname, firstname].filter(Boolean).join(" ") || `#${row.original.user_id ?? row.original.id}`;
        const displayName =
          schoolId === EXTERNAL_STUDENT_LABEL_SCHOOL_ID
            ? buildSchoolStudentLabel(user) || fullName
            : fullName;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <LiveDot
              active={
                liveStudentIds.has(row.original.id) ||
                (row.original.user_id != null && liveStudentIds.has(row.original.user_id))
              }
            />
            <Avatar className="h-9 w-9 rounded-md">
              <AvatarImage src={photo ?? ""} alt={fullName} />
              <AvatarFallback className="rounded-md">{initials}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 truncate font-medium">{displayName}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "score",
      header: t("label-assignment-points"),
      cell: ({ row, table }) => {
        const meta = getStudentTableMeta(table.options.meta);

        const attemptsList = Array.isArray(row.original.attempts)
          ? row.original.attempts
          : [];

        const selectedAttemptId =
          meta?.selectedAttemptByStudentId?.[row.original.id] ?? null;

        const effectiveAttemptId =
          selectedAttemptId ??
          attemptsList.find((a) => a.status === "active")?.id ??
          getLastAttempt(attemptsList)?.id ??
          null;

        const effectiveAttempt =
          effectiveAttemptId != null
            ? attemptsList.find((a) => a.id === effectiveAttemptId) ?? null
            : null;

        const points =
          typeof effectiveAttempt?.points === "number"
            ? effectiveAttempt.points
            : row.original.points ?? 0;

        // Балл доверия показываем ТОЛЬКО для выбранной попытки. Если у попытки
        // нет credibility (null) — это «нет балла» (→ «—»), а НЕ балл строки или
        // другой попытки. row-level credibility берём лишь когда попыток нет вовсе.
        // Балл попытки: сначала top-level credibility (если валиден, >= 0), иначе
        // credibility_verdict.credibility (бэк отдаёт top-level -1 до генерации,
        // а реальный балл — в вердикте). Если попытка есть, но балла нет — null (→ «—»).
        const attemptCredibility = (() => {
          const top = effectiveAttempt?.credibility;
          if (typeof top === "number" && top >= 0) return top;
          const verdictScore = effectiveAttempt?.credibility_verdict?.credibility;
          if (typeof verdictScore === "number") return verdictScore;
          if (effectiveAttempt) return null;
          return typeof row.original.credibility === "number"
            ? row.original.credibility
            : null;
        })();

        const trustScore =
          effectiveAttemptId != null
            ? meta?.trustScoreByAttemptId?.[effectiveAttemptId] ?? null
            : null;

        const hasTrustScore = !meta?.isTrustScoresUnavailable && trustScore != null;

        const isExternalAssignment = meta?.assignmentType === "external";

        const resolvedCredibility = meta?.isTrustScoresUnavailable
          ? attemptCredibility
          : trustScore ?? attemptCredibility;

        // ScoreCell рисует «—» при отрицательном значении (sentinel «нет балла»).
        const credibility = resolvedCredibility ?? -1;

        const attemptsTotal = attemptsList.length;
        const shouldShowAttemptSelector = attemptsTotal > 1;

        const effectiveAttemptIndex =
          effectiveAttemptId != null
            ? attemptsList.findIndex((a) => a.id === effectiveAttemptId)
            : -1;

        const selectedAttemptNumber =
          effectiveAttemptIndex >= 0 ? effectiveAttemptIndex + 1 : 1;

        const radioValue =
          effectiveAttemptId != null ? String(effectiveAttemptId) : "";

        const showCompletedAt = isCompletedTimeAlwaysVisible();
        const completedAtLabel = effectiveAttempt?.finished_at
          ? formatDateTime(effectiveAttempt.finished_at)
          : "-";

        const attemptSelectorContent = (
          <DropdownMenuContent
            align="start"
            className="z-[120] w-fit min-w-0"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuRadioGroup value={radioValue}>
              {attemptsList.map((at, index) => {
                const safeAttemptId = at.id;

                return (
                  <DropdownMenuRadioItem
                    key={String(safeAttemptId)}
                    value={String(safeAttemptId)}
                    onClick={(e) => e.stopPropagation()}
                    onSelect={() => {
                      meta?.onSelectAttempt?.(row.original.id, safeAttemptId);
                    }}
                  >
                    <NumericText className="flex gap-2">
                      {t("label-attempt-number", {
                        number: index + 1,
                      })}
                    </NumericText>
                  </DropdownMenuRadioItem>
                );
              })}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        );

        return (
          <>
            <div className="hidden items-center justify-end gap-2 sm:flex">
              {showCompletedAt && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {completedAtLabel}
                </span>
              )}
              {SHOW_VIOLATIONS && (
                <ViolationsCell
                  assignmentId={Number(meta?.assignmentId ?? 0)}
                  studentId={Number(row.original.id)}
                  attemptId={effectiveAttemptId}
                />
              )}
              <ScoreCell
                points={points}
                credibility={credibility}
                hasTrustScore={hasTrustScore}
                hidePoints={isExternalAssignment}
              />

              {shouldShowAttemptSelector && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-9 w-auto rounded-sm border border-[#E5E5E5] bg-white px-3 py-1.5 text-sm shadow-sm flex items-center gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <NumericText>
                        {t("label-attempt-number", {
                          number: selectedAttemptNumber,
                        })}
                      </NumericText>
                      <ChevronDownIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>

                  {attemptSelectorContent}
                </DropdownMenu>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 sm:hidden">
              {showCompletedAt && (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {completedAtLabel}
                </span>
              )}
              {SHOW_VIOLATIONS && (
                <ViolationsCell
                  assignmentId={Number(meta?.assignmentId ?? 0)}
                  studentId={Number(row.original.id)}
                  attemptId={effectiveAttemptId}
                  compact
                />
              )}
              <ScoreCell
                points={points}
                credibility={credibility}
                hasTrustScore={hasTrustScore}
                compact
                hidePoints={isExternalAssignment}
              />

              {shouldShowAttemptSelector && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-9 min-w-[42px] rounded-sm border border-[#E5E5E5] bg-white px-2 text-sm shadow-sm flex items-center justify-center gap-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <NumericText>{selectedAttemptNumber}</NumericText>
                      <ChevronDownIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>

                  {attemptSelectorContent}
                </DropdownMenu>
              )}
            </div>
          </>
        );
      },
    },
    {
      id: "actions",
      cell: ({ row, table }) => {
        const meta = getStudentTableMeta(table.options.meta);

        return (
          <>
            <ButtonGroup className="hidden sm:flex">
              {meta?.canEditSettings && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    meta?.onOpenSettingsDrawer?.(row.original, "view");
                  }}
                >
                  <FilterIcon size={16} className="h-4 w-4" />
                </Button>
              )}
              {!meta?.isTeacher && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    meta?.onCopyReportLink?.(row.original.id);
                  }}
                >
                  <ShareUpIcon size={16} className="h-4 w-4" />
                </Button>
              )}
            </ButtonGroup>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-sm sm:hidden"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t("label-more")}
                  title={t("label-more")}
                >
                  <SlidersHorizontal size={16} className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="min-w-[220px]"
                onClick={(e) => e.stopPropagation()}
              >
                {meta?.canEditSettings && (
                  <DropdownMenuItem
                    onSelect={() => meta?.onOpenSettingsDrawer?.(row.original, "view")}
                    className="cursor-pointer gap-2"
                  >
                    <FilterIcon size={16} className="h-4 w-4" />
                    {t("btn-task-settings")}
                  </DropdownMenuItem>
                )}
                {!meta?.isTeacher && (
                  <DropdownMenuItem
                    onSelect={() => meta?.onCopyReportLink?.(row.original.id)}
                    className="cursor-pointer gap-2"
                  >
                    <ShareUpIcon size={16} className="h-4 w-4" />
                    {t("btn-open-report")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        );
      },
    },
  ];
